import { useFormikContext } from 'formik';
import {
  Condition,
  FormGroup,
  Grid,
  Trigger,
  useAssetTranslation,
} from '@mss/mss-component-library';
import React, { useContext } from 'react';
import { openNotificationWithIcon } from '@mss/mss-component-library';
import { notification } from 'antd';
import {
  AutomationEventRange,
  TriggerTime,
  TriggerTimePattern,
  AutomationStateChange,
  AutomationStateChangeOperator,
} from './Forms';
import { Select } from './Inputs';
import { AutomationsContext } from './AutomationForm';

type Props = {
  index?: number;
  trigger: Trigger;
  disabled: string;
};

const validateAttributeCondition = (
  triggerType: any,
  condition: any,
  triggerAttributes: any,
  t: any
) => {
  const availableAttributes = triggerAttributes[triggerType];
  if (
    condition.settings.attribute &&
    !availableAttributes.includes(condition.settings.attribute)
  ) {
    openNotificationWithIcon(
      notification.warning,
      t(`automation:Condition error in ID (ID)`, { id: condition.key }),
      t(
        `automation:The attribute (ATTRIBUTE) is an invalid trigger value. The attribute value will be reset!`,
        { attribute: condition.settings.attribute }
      )
    );
    condition.settings.attribute = '';
  }
};

const recursiveConditionValidation = (
  triggerType: any,
  conditions: any,
  triggerAttributes: any,
  t: any
) => {
  if (conditions.length === 0) {
    return;
  }

  conditions.forEach((condition: Condition) => {
    if ((condition.type as string) === 'ATTRIBUTE') {
      validateAttributeCondition(triggerType, condition, triggerAttributes, t);
    }
    if (
      (condition.type as string) === 'CONDITIONS_AND' ||
      (condition.type as string) === 'CONDITIONS_OR'
    ) {
      recursiveConditionValidation(
        triggerType,
        // @ts-ignore
        condition.conditions,
        triggerAttributes,
        t
      );
    }
  });
};

export const TriggerTypeSelector = ({
  index,
  disabled,
}: {
  index?: number;
  disabled?: string;
}) => {
  const { setFieldValue, values } = useFormikContext<any>();
  const { triggerAttributes } = useContext(AutomationsContext);

  const { t } = useAssetTranslation();
  const changeTrigger = (triggerType: any) => {
    index
      ? setFieldValue(`triggers.${index}.settings`, {})
      : setFieldValue(`trigger.settings`, {});
    recursiveConditionValidation(
      triggerType,
      values['conditions'],
      triggerAttributes,
      t
    );
  };
  return (
    <Select
      data-ta={'Triggers_TriggerType'}
      name={`trigger${index ? `.${index}` : ''}.type`}
      onChange={changeTrigger}
      disabled={!!disabled}
    >
      <Select.Option value="EVENT_RANGE">{t('Event range')}</Select.Option>
      <Select.Option value="STATE_CHANGE">{t('State change')}</Select.Option>
      <Select.Option value="STATE_CHANGE_OPERATOR">
        {t('State change operator')}
      </Select.Option>
      <Select.Option value="TIME">{t('Time')}</Select.Option>
      <Select.Option value="TIME_PATTERN">{t('Time pattern')}</Select.Option>
    </Select>
  );
};

const TriggerForm = ({ trigger, disabled }: Props) => {
  const { t } = useAssetTranslation();
  const renderForm = () => {
    switch (trigger.type) {
      case 'EVENT_RANGE':
        return <AutomationEventRange type="trigger" disabled={disabled} />;
      case 'TIME':
        return <TriggerTime type="trigger" disabled={disabled} />;
      case 'TIME_PATTERN':
        return <TriggerTimePattern type="trigger" disabled={disabled} />;
      case 'STATE_CHANGE':
        return <AutomationStateChange type="trigger" disabled={disabled} />;
      case 'STATE_CHANGE_OPERATOR':
        return (
          <AutomationStateChangeOperator type="trigger" disabled={disabled} />
        );
      default:
        return null;
    }
  };

  return (
    <Grid.Row>
      <Grid.Col width={4}>
        <FormGroup
          label={t('Trigger type',{ns:'configuration'})}
          input={<TriggerTypeSelector disabled={disabled} />}
        />
      </Grid.Col>
      <Grid.Col width={8}>
        <Grid.Row>{renderForm()}</Grid.Row>
      </Grid.Col>
    </Grid.Row>
  );
};

export default TriggerForm;
